<template>
  <div class="legal">
    <div class="container py-5">
      <div class="row">
        <div class="col-md-8 mx-auto">
          <h1 class="mb-4">{{ legalTitle }}</h1>

          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-ctorange" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading document...</p>
          </div>

          <div v-else-if="error" class="alert alert-danger">
            <p><strong>Error loading document:</strong></p>
            <p>{{ error.message || 'An unknown error occurred' }}</p>
            <button @click="loadDocument" class="btn btn-ctl mt-3">Try Again</button>
          </div>

          <div v-else-if="!currentDoc" class="alert">
            <p>Document not found.</p>
          </div>

          <div v-else class="ct-card p-4 rounded shadow-sm">
            <div v-html="currentDoc.text" class="legal-content"></div>
            <p class="text-muted mt-4">
              Version {{ currentDoc.version }} | Last modified {{ formatDate(currentDoc.date_modified) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'LegalView',
  data() {
    return {
      documentType: null
    };
  },
  computed: {
    ...mapState('legalDocs', ['currentDoc', 'loading', 'error']),
    legalTitle() {
      if (this.currentDoc) {
        return this.currentDoc.type_display || this.getLegalTitleFromType();
      }
      return this.getLegalTitleFromType();
    }
  },
  methods: {
    ...mapActions('legalDocs', ['fetchLegalDocs']),
    getLegalTitleFromType() {
      const type = this.$route.params.type;
      switch(type) {
        case 'privacy-policy':
          return 'Privacy Policy';
        case 'terms-of-service':
          return 'Terms of Service';
        case 'cookie-policy':
          return 'Cookie Policy';
        case 'eula':
          return 'End User License Agreement';
        default:
          return 'Legal Document';
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(this.$i18n.locale, options);
    },
    loadDocument() {
      const type = this.$route.params.type;
      this.documentType = type;
      console.log('Loading document of type:', type);

      let apiType;
      switch (type) {
        case 'privacy-policy':
          apiType = 'PP';
          break;
        case 'terms-of-service':
          apiType = 'TOS';
          break;
        case 'cookie-policy':
          apiType = 'COOKIE';
          break;
        case 'eula':
          apiType = 'EULA';
          break;
        default:
          apiType = type.toUpperCase();
      }

      console.log('API type:', apiType);

      this.fetchLegalDocs(apiType)
          .then(response => {
            console.log('Legal docs response:', response);
            if (response && response.results.length > 0) {
              console.log('Setting current doc to:', response.results[0]);
              this.$store.commit('legalDocs/SET_CURRENT_DOC', response.results[0]);
            } else {
              console.warn('No legal docs found in response');
            }
          })
          .catch(error => {
            console.error('Error fetching legal document:', error);
          });
    }
  },
  watch: {
    '$route.params.type': {
      handler() {
        this.loadDocument();
      },
      immediate: true
    }
  },
  created() {
    this.setPageTitle(this.getLegalTitleFromType() + ' | CT Grid');
    console.log('LegalView component created');
  }
}
</script>

<style scoped>
/* Style the rich text content coming from the CMS */
.legal-content {
  color: #e0e0e0;
}

/* Style all headings within the legal content */
.legal-content :deep(h1),
.legal-content :deep(h2),
.legal-content :deep(h3),
.legal-content :deep(h4),
.legal-content :deep(h5),
.legal-content :deep(h6) {
  color: #ffffff;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

/* Style all paragraphs within the legal content */
.legal-content :deep(p) {
  color: #e0e0e0;
  margin-bottom: 1rem;
}

/* Style all links within the legal content */
.legal-content :deep(a) {
  color: var(--ct-orange);
  text-decoration: underline;
}

.legal-content :deep(a:hover) {
  color: var(--ct-orange-hover);
}

/* Style all lists within the legal content */
.legal-content :deep(ul),
.legal-content :deep(ol) {
  color: #e0e0e0;
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.legal-content :deep(li) {
  margin-bottom: 0.5rem;
}

/* Style all tables within the legal content */
.legal-content :deep(table) {
  width: 100%;
  margin-bottom: 1rem;
  color: #e0e0e0;
  border-collapse: collapse;
}

.legal-content :deep(th),
.legal-content :deep(td) {
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.legal-content :deep(th) {
  background-color: rgba(255, 255, 255, 0.05);
}

/* Style all blockquotes within the legal content */
.legal-content :deep(blockquote) {
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid var(--ct-orange);
}

/* Style any code blocks within the legal content */
.legal-content :deep(pre),
.legal-content :deep(code) {
  background-color: var(--ct-dark);
  color: #e0e0e0;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
}

/* Fix any other elements that might have default browser styling */
.legal-content :deep(strong),
.legal-content :deep(b) {
  color: #ffffff;
}

.legal-content :deep(em),
.legal-content :deep(i) {
  color: #e0e0e0;
}

/* Ensure the card has dark theme styling */
.ct-card {
  background-color: var(--ct-dark-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: #e0e0e0;
}
</style>